import React, { useCallback, useEffect, useRef, useState } from 'react';
import OutputItem from './OutputItem';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAdd, faXmark, faCheckDouble, faTrashCan, faClipboardCheck } from '@fortawesome/free-solid-svg-icons';
import ItemWrapper from './ItemWrapper';
import Modal from './Modal';
import OutputModal from './OutputModal';
import BulkReviewPanel, { ReviewItem } from './BulkReviewPanel';
import { useItemSelection } from '../hooks/useItemSelection';
import { createNewVersion, clearStatus, isActionableStatus } from '../lib';
import { API_URL } from '../config';
import { ChatToolAction } from './ChatWidget';

type Props = {
  outputRefs: React.MutableRefObject<(HTMLDivElement | null)[]>
  data: DiscoveryData;
  setData: React.Dispatch<React.SetStateAction<DiscoveryData | null>>;
  handleMouseEnter: (entityType: string, entityId: string, data: DiscoveryData) => void;
  handleMouseLeave: (entityType: string, entityId: string, data: DiscoveryData) => void;
  onError: (msg: string) => void;
  onInfo: (msg: string) => void;
  onWaiting: (msg: string) => void;
  backendAvailable: boolean;
  onViewTraceability: (entityType: string, entityId: string) => void;
  chatActions?: ChatToolAction[];
  clearChatActions?: (filter: (a: ChatToolAction) => boolean) => void;
  requestConfirm?: (message: string, onConfirm: () => void) => void;
};

const OutputList: React.FC<Props> = ({ outputRefs, data, setData, handleMouseEnter, handleMouseLeave, onError, onInfo, onWaiting, backendAvailable, onViewTraceability, chatActions, clearChatActions, requestConfirm }) => {

  const dataRef = useRef(data);
  dataRef.current = data;

  const [isOutputDialogVisible, setIsOutputDialogVisible] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [editingOutput, setEditingOutput] = useState<ItemType | null>(null);
  const setOutputRef = useCallback((element: HTMLDivElement, index: number) => { outputRefs.current[index] = element; }, [outputRefs]);

  const { selectedIds: selectedOutputIds, toggleSelection: toggleOutputSelection, clearSelection, selectAll } = useItemSelection(data.outputs.map(o => o.output_id));
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [bulkReviewItems, setBulkReviewItems] = useState<ReviewItem[] | null>(null);

  const reviewableCount = data.outputs.filter(o => isActionableStatus(o.status)).length;

  const openAddModal = () => {
    setModalMode('add');
    setEditingOutput(null);
    setIsOutputDialogVisible(true);
  };

  const openEditModal = (item: ItemType) => {
    setModalMode('edit');
    setEditingOutput(item);
    setIsOutputDialogVisible(true);
  };

  // Handle chat tool actions targeting outputs
  useEffect(() => {
    if (!chatActions?.length || !clearChatActions) return;
    const matching = chatActions.filter(a => a.params.entity_type === 'output');
    if (!matching.length) return;

    clearChatActions(a => a.params.entity_type === 'output');

    // Batch deletes into one confirmation
    const deletes = matching.filter(a => a.tool === 'delete_item');
    if (deletes.length > 0 && requestConfirm) {
      const ids = deletes.flatMap(a =>
        Array.isArray(a.params.item_ids) ? (a.params.item_ids as string[]) : a.params.item_id ? [String(a.params.item_id)] : []
      );
      const items = data.outputs.filter(o => ids.includes(o.output_id));
      if (items.length > 0) {
        const label = items.length === 1
          ? `Delete output "${items[0].text.substring(0, 50)}"?`
          : `Delete ${items.length} outputs?`;
        const idsToDelete = new Set(items.map(i => i.output_id));
        requestConfirm(label, () => {
          setData(prev => prev ? { ...prev, outputs: prev.outputs.filter(o => !idsToDelete.has(o.output_id)) } : prev);
        });
      }
    }

    // Handle first add action
    const add = matching.find(a => a.tool === 'add_item');
    if (add) {
      setModalMode('add');
      setEditingOutput({
        output_id: '',
        text: String(add.params.text || ''),
        related_recommendations: Array.isArray(add.params.related_ids) ? add.params.related_ids as string[] : [],
        type: String(add.params.output_type || 'report') as OutputType['type'],
      } as unknown as ItemType);
      setIsOutputDialogVisible(true);
    }

    if (!add) {
      const edit = matching.find(a => a.tool === 'edit_item');
      if (edit) {
        const output = data.outputs.find(o => o.output_id === edit.params.item_id);
        if (output) {
          setModalMode('edit');
          setEditingOutput({
            ...output,
            text: String(edit.params.new_text || output.text),
          } as unknown as ItemType);
          setIsOutputDialogVisible(true);
        }
      }
    }
  }, [chatActions]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveOutput = async (outputData: OutputType) => {
    if (modalMode === 'add') {
      const newOutput: OutputType = {
        output_id: Math.random().toString(16).slice(2),
        text: outputData.text,
        related_recommendations: outputData.related_recommendations,
        type: outputData.type || 'report',
        version: 1,
        status: 'draft',
        created_at: new Date().toISOString(),
      };
      setData((prevState) => prevState ? ({
        ...prevState,
        outputs: [...prevState.outputs, newOutput]
      }) : prevState);
    } else if (modalMode === 'edit' && outputData.output_id) {
      const existing = data.outputs.find(o => o.output_id === outputData.output_id);
      if (existing && existing.text !== outputData.text) {
          onWaiting('Creating new version...');
          const versioned = createNewVersion(existing, outputData.text) as OutputType;
          const updated = { ...versioned, ...outputData, text: versioned.text, version: versioned.version, status: versioned.status, created_at: versioned.created_at, versions: versioned.versions };
          const updatedOutputs = data.outputs.map(o => o.output_id === outputData.output_id ? updated : o);
          setData((prevState) => prevState ? ({ ...prevState, outputs: updatedOutputs }) : prevState);
          onInfo(`Updated to v${updated.version}. No downstream items (terminal entity).`);
      } else {
        // No text change — update fields and check if links changed
        const linksChanged = existing &&
          JSON.stringify([...existing.related_recommendations].sort()) !== JSON.stringify([...outputData.related_recommendations].sort());

        const updatedOutputs = data.outputs.map(o => o.output_id === outputData.output_id ? { ...o, ...outputData } : o);
        setData((prevState) => prevState ? ({ ...prevState, outputs: updatedOutputs }) : prevState);

        // Mark as needs_refresh if links changed (output is terminal entity)
        if (linksChanged) {
          setData(prev => prev ? ({
            ...prev,
            outputs: prev.outputs.map(o =>
              o.output_id === outputData.output_id ? { ...o, status: 'needs_refresh' as const } : o
            ),
          }) : prev);
          onInfo('Sources changed — output marked for refresh.');
        }
      }
    }
    setIsOutputDialogVisible(false);
  };

  const deleteOutput = (outputId: string) => {
    const updatedOutputs = data.outputs.filter((output) => output.output_id!== outputId);
    setData((prevState) => prevState ? ({
        ...prevState,
        outputs: updatedOutputs
      }) : prevState);
  };

  const handleClearStatus = (outputId: string) => {
    setData((prevState) => prevState ? clearStatus(prevState, 'output', outputId) : prevState);
  };

  // Bulk review
  const handleSelectReviewable = () => {
    const reviewable = data.outputs.filter(o => isActionableStatus(o.status));
    selectAll(reviewable.map(o => o.output_id));
    if (reviewable.length > 0) {
      const el = document.getElementById(`output-${reviewable[0].output_id}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const handleBulkReview = () => {
    const reviewable = data.outputs.filter(
      o => selectedOutputIds.has(o.output_id) && isActionableStatus(o.status)
    );
    const items = reviewable
      .map(output => {
        const parentRec = data.recommendations.find(r => output.related_recommendations.includes(r.recommendation_id));
        if (!parentRec) return null;
        const oldText = parentRec.versions?.length
          ? parentRec.versions[parentRec.versions.length - 1].text
          : '';
        return {
          id: output.output_id,
          entityType: 'output',
          currentText: output.text,
          upstreamOldText: oldText,
          upstreamNewText: parentRec.text || '',
          upstreamEntityType: 'recommendation',
          goal: data.goal || '',
          outputType: output.type as string,
        } as ReviewItem;
      })
      .filter((x): x is ReviewItem => x !== null);
    if (items.length === 0) { onError('No reviewable outputs with valid upstream found.'); return; }
    setBulkReviewItems(items);
  };

  const handleBulkReviewAccept = async (id: string, _entityType: string, newText: string) => {
    const latest = dataRef.current;
    const existing = latest.outputs.find(o => o.output_id === id);
    if (!existing) return;
    const versioned = createNewVersion(existing, newText) as OutputType;
    const updated = { ...versioned, type: existing.type };
    let updatedData = { ...latest, outputs: latest.outputs.map(o => o.output_id === id ? updated : o) };
    updatedData = clearStatus(updatedData, 'output', id);
    dataRef.current = updatedData;
    setData(updatedData);
    onInfo(`Output updated to v${updated.version}.`);
  };

  const handleBulkReviewReject = (id: string) => {
    setData(prev => prev ? clearStatus(prev, 'output', id) : prev);
  };

  return (
    <div className="column outputs">
      <div className="column-sticky-top">
        <div className="column-header">
          <h2>📤Outputs</h2>
          {reviewableCount > 0 && <button className="review-select-btn" onClick={handleSelectReviewable}>{reviewableCount} to review</button>}
          {data.outputs.length > 0 && selectedOutputIds.size < data.outputs.length && (
            <button className="select-all-button" onClick={() => selectAll(data.outputs.map(o => o.output_id))} title="Select all outputs">
              <FontAwesomeIcon icon={faCheckDouble} /> Select All
            </button>
          )}
          <button className="header-add-button" onClick={openAddModal} title="Add Output"><FontAwesomeIcon icon={faAdd} /></button>
        </div>
        <div className={`toolbar-wrapper${selectedOutputIds.size > 0 ? ' toolbar-wrapper-open' : ''}`}>
          <div className="selection-toolbar">
            <span>{selectedOutputIds.size} output(s) selected</span>
            {data.outputs.some(o => selectedOutputIds.has(o.output_id) && isActionableStatus(o.status)) && backendAvailable && (
              <button className="toolbar-review-btn" onClick={handleBulkReview} title="AI review proposals for flagged items">
                <FontAwesomeIcon icon={faClipboardCheck} />
                {' '}Review
              </button>
            )}
            <button onClick={() => setConfirmBulkDelete(true)}>
              <FontAwesomeIcon icon={faTrashCan} />
              {' '}Delete
            </button>
            <button onClick={clearSelection}>
              <FontAwesomeIcon icon={faXmark} />
              {' '}Clear
            </button>
          </div>
        </div>
      </div>
      {data.outputs.length === 0 && (
        <p className="empty-state-hint">Select recommendations to formulate deliverables.</p>
      )}
      {data.outputs.map((output, index) => (
        <div
          key={output.output_id}
          onClick={() => toggleOutputSelection(output.output_id)}
          className={selectedOutputIds.has(output.output_id) ? 'item-selectable selected' : 'item-selectable'}
        >
          <ItemWrapper
            id={"output-" + output.output_id}
            index={index}
            item={output}
            setItemRef={setOutputRef}
            handleMouseEnter={() => handleMouseEnter("output", output.output_id, data)}
            handleMouseLeave={() => handleMouseLeave("output", output.output_id, data)}
            openEditModal={openEditModal}
            onViewTraceability={() => onViewTraceability("output", output.output_id)}
            onClearStatus={() => handleClearStatus(output.output_id)}
            onDelete={() => requestConfirm?.('Delete this output?', () => deleteOutput(output.output_id))}
          >
            <OutputItem
              output={output}
            />
          </ItemWrapper>
        </div>
      ))}
      <OutputModal
        mode={modalMode}
        isDialogVisible={isOutputDialogVisible}
        closeDialog={() => setIsOutputDialogVisible(false)}
        saveOutput={saveOutput}
        deleteOutput={deleteOutput}
        outputData={editingOutput as OutputType}
        recommendations={data.recommendations}
      />
      <Modal isVisible={confirmBulkDelete} onClose={() => setConfirmBulkDelete(false)} maxWidth="400px">
        <p style={{ margin: '0 0 1em' }}>Delete {selectedOutputIds.size} selected output(s)?</p>
        <div className="modal-actions">
          <div className="modal-action-group-left">
            <button className="modal-action-save" onClick={() => { setData(prev => prev ? { ...prev, outputs: prev.outputs.filter(o => !selectedOutputIds.has(o.output_id)) } : prev); clearSelection(); setConfirmBulkDelete(false); }}>Confirm</button>
          </div>
          <div className="modal-action-group-right">
            <button className="modal-action-close" onClick={() => setConfirmBulkDelete(false)}>Cancel</button>
          </div>
        </div>
      </Modal>
      {bulkReviewItems && (
        <BulkReviewPanel
          items={bulkReviewItems}
          onAccept={handleBulkReviewAccept}
          onReject={handleBulkReviewReject}
          onClose={() => setBulkReviewItems(null)}
        />
      )}
    </div>
  );
};

export default OutputList;
